import React from "react";
import {useTranslation} from "react-i18next";
import Container from "@material-ui/core/Container";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import {Box, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Typography} from "@material-ui/core";
import {YoutubeSearchedFor} from "@material-ui/icons";
import withWidth, {WithWidthProps} from '@material-ui/core/withWidth';
import RoutingPointsStepper from "./RoutingPointsStepper";
import {CommonShipmentsPageProps} from "./CommonShipmentsComponents";
import IconButton from '@material-ui/core/IconButton';
import {useNavigate, useLocation} from "react-router-dom";
import queryString from 'query-string';
import {COMMUNICATOR_PAGE_PATH, EVENT_UPDATE_PAGE_PATH} from "../../../layouts/navigation/Navigator";

export interface IRoutingPoints {
    routingType: string
    routingDate?: string
    reached: boolean
    visible: boolean
    location?: string
}

export interface IShipment {
    shipmentId: number
    spotID: string
    reference?: string
    uuid: string
    shipper: string
    consignee: string
    modality: string
    status: string
    routingPoints: IRoutingPoints[]
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
        },
        nothingFoundRoot: {
            flex: 1,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
        },
        modality: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            "& img": {
                height: 40
            }
        },
        lineItemContextContainer: {
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%"
        },
        shipmentInfoContainer: {
            display: "flex",
            alignItems: "center",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        shipmentInfoCard: {
            flex: 1
        },
        caption: {
            fontWeight: "bold",
            marginRight: 5
        },
        value: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: '11rem'
        }
    }),
);

interface IShipmentListProps extends CommonShipmentsPageProps {
    shipments: IShipment[]
}

interface IShipmentListItem {
    shipment: IShipment
}


const ShipmentList: React.FC<IShipmentListProps & WithWidthProps> = ({shipments, width, detailsPagePath, Icon}) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const navigate = useNavigate();

    const refs = shipments.reduce((acc, value) => {
        acc[value.spotID] = React.createRef();
        return acc;
    }, {});

    const isMobile = width === 'xs' || width === "sm";

    const location = useLocation();
    const {load, query, scroll} = queryString.parse(location.search);

    const scrollKey = Array.isArray(scroll) ? scroll[0] : scroll;

    React.useEffect(() => {
        if (scrollKey && refs && refs[scrollKey] && refs[scrollKey].current) {
            refs[scrollKey].current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    // This has been disabled so as to ensure that the jenkins
    // build passes. The fact is, this specific hook should only
    // be performed at the initial render and when the scroll variable
    // is modified, otherwise, when loading more shipments, the scrolling
    // behaviour causes the client to scroll back up to a previously
    // opened shipment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scroll]);


    if (!shipments || shipments.length < 1) {
        return <Container>
            <div className={classes.nothingFoundRoot}>
                <Typography variant="h5">{t("shipment.no-shipments-found")}</Typography>
                <YoutubeSearchedFor fontSize="large"/>
            </div>
        </Container>
    }


    function redirectToDetailsView(spotID: string, uuid: string, shipmentId: number) {
        navigate({search: `load=${load}&query=${query ? query : ""}&scroll=${spotID}`});

        const pathname = (detailsPagePath === COMMUNICATOR_PAGE_PATH || detailsPagePath === EVENT_UPDATE_PAGE_PATH) ? `${detailsPagePath}/${uuid}/${shipmentId}` : `${detailsPagePath}/${uuid}`;
        navigate({
            pathname,
            search: `redirect=${encodeURIComponent(location.pathname + `?load=${load}&query=${query ? query : ""}&scroll=${spotID}`)}`
        });
    }

    return <Container>
        <div className={classes.root}>
            <List>

                {shipments && shipments.map((shipment) => {
                    const {modality, routingPoints, spotID, uuid, shipmentId} = shipment;
                    return (<ListItem button key={spotID} onClick={() => {
                            redirectToDetailsView(spotID, uuid, shipmentId);
                        }}
                                      ref={refs[spotID]}>
                            <ListItemIcon>
                                <div className={classes.modality}>
                                    <img src={`/assets/icons/modalities/${modality.toLowerCase()}.png`} alt={modality}/>
                                    <Typography variant="caption">{t("core.modality." + modality)}</Typography>
                                </div>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <div className={classes.lineItemContextContainer}>
                                        <Box component="div" className={classes.shipmentInfoCard}>

                                            <ShipmentListItem
                                                shipment={shipment}
                                                width={width}/>

                                        </Box>
                                        {!isMobile && <RoutingPointsStepper routingPoints={routingPoints}/>
                                        }
                                    </div>
                                }
                            />
                            <ListItemSecondaryAction>
                                <IconButton size="medium" color="primary">
                                    {Icon}
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>

                    );
                })}
            </List>
        </div>
    </Container>
};

const ShipmentListItem: React.FC<IShipmentListItem & WithWidthProps> = ({shipment, width}) => {

    const {spotID, reference, shipper, consignee, status} = shipment;
    const {t} = useTranslation();
    const classes = useStyles();


    const propertyValue = [{
        property: "spotId",
        value: spotID
    },
        {
            property: "status",
            value: status
        },
        {
            property: "reference",
            value: reference
        },
        {
            property: "consignee",
            value: consignee
        }, {
            property: "shipper",
            value: shipper
        }].filter(propValue => !!propValue.value).map(propValue => {
        if (propValue.property === "status") {
            propValue.value = t("shipment.tracking-status-range." + propValue.value);
        }
        return propValue;
    });


    return <React.Fragment>
        {propertyValue.map(({property, value}) => {

            return <div className={classes.shipmentInfoContainer} style={{width: width === "xs" ? '12rem' : '25rem'}}
                        key={property}>
                <Typography
                    variant="body1"
                    color="textPrimary"
                    className={classes.caption}
                >
                    {t("shipment." + property) + ":"}
                </Typography>
                <Typography
                    noWrap={true}
                    variant="body2"
                    color="textPrimary"
                >
                    {value}
                </Typography>
            </div>
        })}

    </React.Fragment>
};

export default withWidth()(ShipmentList);